import { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Button, Form, Card } from 'react-bootstrap';
import consoleLogger from '../services/consoleLogger.js';
import { useConsoleViewer } from '../contexts/ConsoleViewerContext.jsx';
import { valueToLogString } from '../utils/serializeLogValue.js';

function ConsoleViewer({ isVisible, onClose }) {
  const { panelWidth: contextPanelWidth, updatePanelWidth } = useConsoleViewer();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    level: '',
    source: '',
    search: '',
    limit: 500
  });
  const [showClearModal, setShowClearModal] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const isDraggingRef = useRef(false);
  const logsContainerRef = useRef(null);
  const previousLogsLengthRef = useRef(0);



  // Load logs with current filters
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const options = {
        limit: filters.limit === 'all' ? null : filters.limit,
        level: filters.level || null,
        source: filters.source || null
      };

      const fetchedLogs = await consoleLogger.getLogs(options);
      setLogs(fetchedLogs);

      // Check if we should auto-scroll to bottom
      if (logsContainerRef.current && fetchedLogs.length > previousLogsLengthRef.current) {
        const container = logsContainerRef.current;
        const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 10; // 10px tolerance

        if (isAtBottom) {
          // User is at bottom, auto-scroll to show new logs
          setTimeout(() => {
            container.scrollTop = container.scrollHeight;
          }, 0);
        }
      }

      previousLogsLengthRef.current = fetchedLogs.length;
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const fetchedStats = await consoleLogger.getStats();
      setStats(fetchedStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Load data on mount and when filters change
  useEffect(() => {
    if (isVisible) {
      loadLogs();
      loadStats();
    }
  }, [isVisible, loadLogs, loadStats]);

  // Auto-refresh logs when console viewer is visible
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      loadLogs();
      loadStats();
    }, 1000); // Refresh every second

    return () => clearInterval(interval);
  }, [isVisible, loadLogs, loadStats]);

  // Handle drag start
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Set dragging state immediately using ref
    isDraggingRef.current = true;
    setIsDragging(true);

    const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    dragStartX.current = clientX;
    dragStartWidth.current = contextPanelWidth;

    // Add event listeners
    if (e.type === 'mousedown') {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
    } else {
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
    }
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const deltaX = dragStartX.current - clientX;
    const newWidth = Math.max(300, Math.min(window.innerWidth * 0.95, dragStartWidth.current + deltaX));
    updatePanelWidth(newWidth);
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    isDraggingRef.current = false;
    setIsDragging(false);

    // Remove event listeners
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchmove', handleDragMove);
    document.removeEventListener('touchend', handleDragEnd);
  };

  // Filter logs by search term
  const filteredLogs = logs.filter(log => {
    if (!filters.search) return true;

    const searchTerm = filters.search.toLowerCase();
    return (
      log.message.toLowerCase().includes(searchTerm) ||
      log.location?.toLowerCase().includes(searchTerm) ||
      log.source.toLowerCase().includes(searchTerm)
    );
  });

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all logs
  const handleClearLogs = async () => {
    try {
      await consoleLogger.clearLogs();
      setShowClearModal(false);
      loadLogs();
      loadStats();
    } catch (error) {
      console.error('Error clearing logs:', error);
    }
  };

  // Delete old logs
  const handleDeleteOldLogs = async () => {
    try {
      const deletedCount = await consoleLogger.deleteOldLogs(7);
      loadLogs();
      loadStats();
    } catch (error) {
      console.error('Error deleting old logs:', error);
    }
  };

  // Toggle expand all
  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandedLogs(new Set());
      setExpandAll(false);
    } else {
      setExpandedLogs(new Set(filteredLogs.map(log => log.id)));
      setExpandAll(true);
    }
  };

  // Toggle individual log expansion
  const toggleLogExpansion = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  // Export logs to CSV
  const handleExportLogs = () => {
    try {
      // Create CSV content
      const csvHeaders = ['Timestamp', 'Level', 'Message', 'Source', 'Location', 'Data'];
      const csvRows = [csvHeaders];

      // Add filtered logs to CSV
      filteredLogs.forEach(log => {
        const row = [
          new Date(log.timestamp).toISOString(),
          log.level,
          `"${log.message.replace(/"/g, '""')}"`, // Escape quotes in message
          log.source,
          log.location || '',
          log.data && log.data.length > 0 ? `"${JSON.stringify(log.data).replace(/"/g, '""')}"` : ''
        ];
        csvRows.push(row);
      });

      const csvContent = csvRows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `console-logs-${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting logs:', error);
    }
  };

  // Get background color for log level
  const getLogBackgroundColor = (level) => {
    switch (level) {
      case 'error': return '#f8d7da';
      case 'warn': return '#fff3cd';
      case 'info': return '#d1ecf1';
      default: return '#ffffff';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const timeString = date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    // Show full date if panel is wide enough (more than 800px)
    if (contextPanelWidth > 800) {
      const dateString = date.toISOString().slice(0, 10); // YYYY-MM-DD
      const timezone = date.toLocaleTimeString('en-US', {
        timeZoneName: 'short'
      }).split(' ').pop(); // Extract timezone abbreviation
      return `${dateString}T${timeString} ${timezone}`;
    }

    return timeString;
  };

  // Format log data
  const formatLogData = (data) => {
    if (!data || data.length === 0) return null;

    return data.map((item, index) => {
      if (typeof item === 'object' && item !== null) {
        return <pre key={index} className="mb-1" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{valueToLogString(item)}</pre>;
      }
      return <span key={index} className="me-2">{String(item)}</span>;
    });
  };

  // Truncate text
  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (!isVisible) return null;

  return (
    <div
      className="console-side-panel"
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-2px 0 10px rgba(0,0,0,0.1)'
      }}
    >
      {/* Drag Handle */}
      <div
        className="console-drag-handle"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '16px',
          height: '100%',
          backgroundColor: '#f8f9fa',
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1051,
          borderRight: '2px solid #dee2e6',
          userSelect: 'none',
          touchAction: 'none'
        }}
      >
        <div style={{
          width: '4px',
          height: '60px',
          backgroundColor: '#adb5bd',
          borderRadius: '2px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ width: '2px', height: '2px', backgroundColor: '#6c757d', borderRadius: '1px' }}></div>
          <div style={{ width: '2px', height: '2px', backgroundColor: '#6c757d', borderRadius: '1px' }}></div>
          <div style={{ width: '2px', height: '2px', backgroundColor: '#6c757d', borderRadius: '1px' }}></div>
        </div>
      </div>

      {/* Header */}
      <div
        className="console-header"
        style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginLeft: '16px' // Account for drag handle
        }}
      >
        <h6 className="mb-0">Console Logs</h6>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onClose}
          style={{ border: 'none', fontSize: '1.5rem', lineHeight: 1 }}
        >
          ×
        </Button>
      </div>

      {/* Content */}
      <div className="console-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', marginLeft: '16px' }}>
        {/* Filters */}
        <Card className="border-0 border-bottom rounded-0">
          <Card.Body className="py-2">
            <Row className="g-2">
              <Col xs={12} md={6}>
                <Form.Select
                  size="sm"
                  value={filters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                >
                  <option value="">All Levels</option>
                  <option value="log">Log</option>
                  <option value="warn">Warning</option>
                  <option value="error">Error</option>
                  <option value="info">Info</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={6}>
                <Form.Select
                  size="sm"
                  value={filters.source}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                >
                  <option value="">All Sources</option>
                  <option value="app">App</option>
                  <option value="service-worker">Service Worker</option>
                  <option value="component">Component</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={6}>
                <Form.Control
                  type="text"
                  placeholder="Search logs..."
                  size="sm"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Select
                  size="sm"
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                >
                  <option value={100}>100 logs</option>
                  <option value={500}>500 logs</option>
                  <option value={1000}>1000 logs</option>
                  <option value="all">All logs ({stats?.total || 0})</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Statistics */}
        {stats && (
          <Card className="border-0 border-bottom rounded-0 bg-light">
            <Card.Body className="py-1">
              <Row className="g-1 text-center">
                <Col xs={3}>
                  <small className="text-muted">Total</small>
                  <div className="fw-bold">{stats.total}</div>
                </Col>
                <Col xs={3}>
                  <small className="text-muted">Errors</small>
                  <div className="fw-bold text-danger">{stats.byLevel.error || 0}</div>
                </Col>
                <Col xs={3}>
                  <small className="text-muted">Warnings</small>
                  <div className="fw-bold text-warning">{stats.byLevel.warn || 0}</div>
                </Col>
                <Col xs={3}>
                  <small className="text-muted">App</small>
                  <div className="fw-bold">{stats.bySource.app || 0}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Actions */}
        <Card className="border-0 border-bottom rounded-0">
          <Card.Body className="py-1">
            <div className="d-flex gap-1 flex-wrap">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowClearModal(true)}
              >
                Clear All
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={handleDeleteOldLogs}
              >
                Delete Old (7 days)
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                onClick={toggleExpandAll}
              >
                {expandAll ? 'Collapse All' : 'Expand All'}
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleExportLogs}
              >
                Export CSV
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Logs */}
        <div
          ref={logsContainerRef}
          className="console-logs-container"
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#f8f9fa',
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          }}
        >
          {filteredLogs.length === 0 ? (
            <div className="text-center text-muted py-5">
              {loading ? 'Loading logs...' : 'No logs found'}
            </div>
          ) : (
            <div className="console-logs-list">
              {filteredLogs.map((log) => {
                const isExpanded = expandedLogs.has(log.id);
                const backgroundColor = getLogBackgroundColor(log.level);

                return (
                  <div key={log.id} className="console-log-item">
                    {/* Single line log entry */}
                    <div
                      className="log-entry-line"
                      onClick={() => toggleLogExpansion(log.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: backgroundColor,
                        borderBottom: '1px solid #dee2e6',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        minHeight: '2rem'
                      }}
                    >
                      {/* Time */}
                      <div style={{ minWidth: '80px', color: '#666' }}>
                        {formatTimestamp(log.timestamp)}
                      </div>

                      {/* Log message */}
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <span style={{ color: log.level === 'error' ? '#721c24' : '#000' }}>
                          {truncateText(log.message, 200)}
                        </span>
                      </div>

                      {/* Location */}
                      {log.location && (
                        <div style={{ minWidth: '120px', textAlign: 'right', color: '#666', fontSize: '0.8rem' }}>
                          {truncateText(log.location, 15)}
                        </div>
                      )}

                      {/* Expand indicator */}
                      <div style={{ minWidth: '20px', textAlign: 'center' }}>
                        {isExpanded ? '▼' : '▶'}
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div
                        className="log-details"
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: '#fff',
                          borderBottom: '1px solid #dee2e6',
                          fontSize: '0.8rem'
                        }}
                      >
                        {/* Full message */}
                        <div className="mb-2">
                          <strong>Message:</strong> {log.message}
                        </div>

                        {/* Location */}
                        {log.location && (
                          <div className="mb-2">
                            <strong>Location:</strong> {log.location}
                          </div>
                        )}

                        {/* Source */}
                        <div className="mb-2">
                          <strong>Source:</strong> {log.source}
                        </div>

                        {/* Data */}
                        {log.data && log.data.length > 0 && (
                          <div>
                            <strong>Data:</strong>
                            <div className="mt-1">
                              {formatLogData(log.data)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearModal && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1060,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '400px',
              width: '90%'
            }}
          >
            <h5>Clear All Logs</h5>
            <p className="mb-3">Are you sure you want to clear all console logs? This action cannot be undone.</p>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" size="sm" onClick={() => setShowClearModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleClearLogs}>
                Clear All Logs
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsoleViewer; 